import { Button, Drawer, Dropdown, Input, List, Modal, Skeleton, Typography } from 'antd';
import type { MenuProps } from 'antd';
import React from 'react';
import { StyledItem, StyledListItem } from './styled';
import { TOOLTIP_LABEL_FORMAT } from '@src/utils/constants';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import useGetSessions from '../hooks/useGetSessions';
import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PushpinOutlined,
  PushpinFilled,
} from '@ant-design/icons';
import { useDeleteSession } from '../hooks/useDeleteSession';
import { useUpdateSessionMetadata } from '../hooks/useUpdateSessionMetadata';
import useCleanupDashboardContext from '../hooks/useCleanupDashboardContext';
import { getSessionId } from '../NexaStore';
import {
  categorizeSessions,
  separatePinnedSessions,
  getSessionDisplayTitle,
  getSessionFullTitle,
  MAX_PINNED_SESSIONS,
  SessionData,
} from './utils';
const { baseUrl } = window.conviva;

const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 100;

interface ListItemProps {
  item: SessionData;
  handleClose: () => void;
  pinnedCount: number;
}
interface SessionsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface SessionCategoryProps {
  title: string;
  sessions: SessionData[];
  handleClose: () => void;
  pinnedCount: number;
}

export const ListItem = ({ item, handleClose, pinnedCount }: ListItemProps) => {
  const navigate = useNavigate();
  const deleteMutation = useDeleteSession();
  const updateMetadataMutation = useUpdateSessionMetadata();
  const id = item.session_id;

  // Rename modal state
  const [renameModalOpen, setRenameModalOpen] = React.useState(false);
  const [editedName, setEditedName] = React.useState(item.display_name || '');

  // session changes causes re-render, so we don't need to subscribe to the store here
  const currentSessionId = getSessionId();
  const isCurrentSession = currentSessionId === id;

  const cleanupDashboardContext = useCleanupDashboardContext();

  const handleRecordClick = () => {
    cleanupDashboardContext(id);
    navigate(`${baseUrl}/dashboard/nexa/${id}`);
    handleClose();
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete session',
      content: 'Are you sure you want to delete this session?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => deleteMutation.mutateAsync({ sessionId: id }),
    });
  };

  const handleTogglePin = () => {
    updateMetadataMutation.mutateAsync({
      sessionId: id,
      is_pinned: !item.is_pinned,
    });
  };

  const trimmedName = editedName.trim();
  const isNameValid =
    trimmedName.length >= MIN_NAME_LENGTH && trimmedName.length <= MAX_NAME_LENGTH;

  const handleSaveName = () => {
    if (isNameValid) {
      updateMetadataMutation.mutateAsync({
        sessionId: id,
        display_name: trimmedName,
      });
      setRenameModalOpen(false);
    }
  };

  const openRenameModal = () => {
    setEditedName(item.display_name || item.last_message || '');
    setRenameModalOpen(true);
  };

  const isPinned = item.is_pinned || false;
  const canPin = isPinned || pinnedCount < MAX_PINNED_SESSIONS;
  const fullTitle = getSessionFullTitle(item);
  const displayTitle = getSessionDisplayTitle(item);

  // Dropdown menu items
  const menuItems: MenuProps['items'] = [
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: 'Rename',
      onClick: openRenameModal,
    },
    {
      key: 'pin',
      icon: isPinned ? <PushpinFilled /> : <PushpinOutlined />,
      label: isPinned ? 'Unpin' : 'Pin',
      onClick: handleTogglePin,
      disabled: !canPin,
      title: !canPin ? `Unpin a session to pin another (max ${MAX_PINNED_SESSIONS})` : undefined,
    },
    { type: 'divider' },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: handleDelete,
      disabled: isCurrentSession,
      title: isCurrentSession ? 'Cannot delete current session' : undefined,
    },
  ];

  return (
    <StyledListItem
      key={id}
      extra={
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button size="small" icon={<MoreOutlined />} type="text" />
        </Dropdown>
      }
      style={{ padding: '16px 0px' }}
    >
      <List.Item.Meta
        title={
          <StyledItem onClick={handleRecordClick} title={fullTitle}>
            {displayTitle}
          </StyledItem>
        }
        description={moment(item.timestamp).format(TOOLTIP_LABEL_FORMAT)}
      />

      {/* Rename Modal */}
      <Modal
        title="Rename Session"
        open={renameModalOpen}
        onOk={handleSaveName}
        onCancel={() => setRenameModalOpen(false)}
        okText="Save"
        okButtonProps={{ disabled: !isNameValid }}
        width={400}
      >
        <Input
          value={editedName}
          onChange={(e) => setEditedName(e.target.value)}
          placeholder="Enter session name (3-100 characters)"
          maxLength={MAX_NAME_LENGTH}
          onPressEnter={isNameValid ? handleSaveName : undefined}
          autoFocus
          status={editedName && !isNameValid ? 'error' : undefined}
        />
        {editedName && !isNameValid && (
          <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            Name must be between {MIN_NAME_LENGTH} and {MAX_NAME_LENGTH} characters
          </Typography.Text>
        )}
      </Modal>
    </StyledListItem>
  );
};

const SessionCategory = ({ title, sessions, handleClose, pinnedCount }: SessionCategoryProps) => {
  if (sessions.length === 0) return null;

  return (
    <>
      <Typography.Title level={5} style={{ marginTop: 16 }}>
        {title}
      </Typography.Title>
      <List
        size="small"
        dataSource={sessions}
        renderItem={(item: SessionData) => (
          <ListItem item={item} handleClose={handleClose} pinnedCount={pinnedCount} />
        )}
      />
    </>
  );
};

const Sessions = ({ open, setOpen }: SessionsProps) => {
  const handleClose = () => {
    setOpen(false);
  };

  const { data = [], isFetching } = useGetSessions({ open });

  // Separate pinned and unpinned sessions
  const { pinned, unpinned } = React.useMemo(() => {
    return separatePinnedSessions(data as SessionData[]);
  }, [data]);

  // Categorize unpinned sessions by date
  const categorized = React.useMemo(() => {
    return categorizeSessions(unpinned);
  }, [unpinned]);

  const pinnedCount = pinned.length;

  return (
    <Drawer
      title="All Sessions"
      placement="left"
      closable={true}
      onClose={handleClose}
      open={open}
      width={450}
    >
      {isFetching ? (
        <Skeleton active />
      ) : (
        <>
          {/* Pinned Section */}
          {pinned.length > 0 && (
            <>
              <Typography.Title level={5} style={{ marginTop: 0 }}>
                <PushpinFilled style={{ marginRight: 8, color: '#1890ff' }} />
                Pinned
              </Typography.Title>
              <List
                size="small"
                dataSource={pinned}
                renderItem={(item: SessionData) => (
                  <ListItem item={item} handleClose={handleClose} pinnedCount={pinnedCount} />
                )}
                style={{ maxHeight: 300, overflow: 'auto', marginBottom: 16 }}
              />
            </>
          )}

          {/* Recent Sessions by Date */}
          <SessionCategory
            title="Today"
            sessions={categorized.today}
            handleClose={handleClose}
            pinnedCount={pinnedCount}
          />
          <SessionCategory
            title="Yesterday"
            sessions={categorized.yesterday}
            handleClose={handleClose}
            pinnedCount={pinnedCount}
          />
          <SessionCategory
            title="Last Week"
            sessions={categorized.lastWeek}
            handleClose={handleClose}
            pinnedCount={pinnedCount}
          />
          <SessionCategory
            title="Older"
            sessions={categorized.older}
            handleClose={handleClose}
            pinnedCount={pinnedCount}
          />

          {data.length === 0 && (
            <Typography.Text type="secondary">No sessions found</Typography.Text>
          )}
        </>
      )}
    </Drawer>
  );
};

export default Sessions;
